import React from "react";
import { Plus, Heart } from "lucide-react";

const MenuItemCard = ({
  item,
  onAddToCart,
  addingToCart,
  handleFavoriteToggle,
  favorites,
  showType,
  cartItems = [],
  updateQuantity,
  removeItem,
}) => {
  const cartItem = cartItems.find((ci) => ci.id === item.id);
  const quantity = cartItem ? cartItem.quantity : 0;
  return (
    <div className="group bg-white rounded-2xl shadow-md border border-gray-100 overflow-hidden hover:shadow-2xl transition-all duration-300 hover:-translate-y-1 flex flex-col w-full max-w-sm">
      <div className="relative overflow-hidden aspect-[4/3]">
        <img
          src={
            item.image ||
            `https://images.unsplash.com/photo-1546069901-ba9599a7e63c?w=400&h=300&fit=crop&crop=center`
          }
          alt={item.name}
          className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700 ease-in-out"
          onError={(e) => {
            e.target.src = `https://via.placeholder.com/400x300/F3F4F6/6B7280?text=${encodeURIComponent(
              item.name || "Item"
            )}`;
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>

        <button
          onClick={() => handleFavoriteToggle(item.id)}
          className="absolute top-4 right-4 w-10 h-10 bg-white/95 backdrop-blur-sm rounded-full flex items-center justify-center shadow-lg opacity-0 group-hover:opacity-100 transition-all duration-300 hover:bg-white hover:scale-110 z-10"
        >
          <Heart
            className={`w-5 h-5 transition-all duration-200 ${
              favorites.has(item.id)
                ? "fill-red-500 text-red-500 scale-110"
                : "text-gray-600 hover:text-red-500"
            }`}
          />
        </button>

        {item.popular && (
          <div className="absolute top-4 left-4 bg-orange-500 text-white text-xs font-bold px-3 py-1 rounded-full shadow-lg">
            🔥 Popular
          </div>
        )}
      </div>

      <div className="p-4 flex flex-col flex-grow">
        <div className="flex justify-between items-start mb-2">
          <h3 className="font-bold text-gray-900 text-lg group-hover:text-primary-600 transition-colors duration-200 line-clamp-2">
            {item.name || "Unnamed Item"}
          </h3>
          <div className="text-right ml-2 flex-shrink-0">
            <span className="text-lg font-bold text-primary-600">
              ${Number(item.price || 0).toFixed(2)}
            </span>
            {item.originalPrice && (
              <div className="text-xs text-gray-500 line-through">
                ${Number(item.originalPrice).toFixed(2)}
              </div>
            )}
          </div>
        </div>

        <p className="text-gray-600 text-sm mb-3 leading-relaxed line-clamp-2 flex-grow">
          {item.description ||
            "A carefully crafted dish made with the finest ingredients"}
        </p>

        {item.tags && item.tags.length > 0 && (
          <div className="flex flex-wrap gap-1.5 mb-3">
            {item.tags.map((tag, tagIndex) => (
              <span
                key={tagIndex}
                className="bg-gray-50 text-gray-600 text-[10px] px-2 py-0.5 rounded-full font-medium border border-gray-100"
              >
                {tag}
              </span>
            ))}
          </div>
        )}

        {quantity > 0 ? (
          <div className="flex items-center gap-2 mt-2">
            <button
              onClick={() => updateQuantity(item.id, quantity - 1)}
              className="px-3 py-1 rounded-lg bg-gray-100 hover:bg-gray-200 text-gray-700 font-bold text-lg"
              disabled={quantity <= 1}
              aria-label="Decrease quantity"
            >
              -
            </button>
            <span className="font-semibold text-lg min-w-[2ch] text-center">
              {quantity}
            </span>
            <button
              onClick={() => updateQuantity(item.id, quantity + 1)}
              className="px-3 py-1 rounded-lg bg-gray-100 hover:bg-gray-200 text-gray-700 font-bold text-lg"
              aria-label="Increase quantity"
            >
              +
            </button>
            <button
              onClick={() => removeItem(item.id)}
              className="ml-2 px-2 py-1 rounded-lg bg-red-100 hover:bg-red-200 text-red-600 text-xs font-semibold"
              aria-label="Remove from cart"
            >
              Remove
            </button>
          </div>
        ) : (
          <button
            onClick={() => onAddToCart(item)}
            disabled={addingToCart === item.id}
            className={`w-full font-semibold py-2.5 px-4 rounded-xl transition-all duration-300 flex items-center justify-center space-x-2 ${
              addingToCart === item.id
                ? "bg-green-500 text-white scale-95"
                : "bg-gradient-to-r from-primary-600 to-primary-700 hover:from-primary-700 hover:to-primary-800 text-white shadow hover:shadow-lg hover:-translate-y-0.5"
            }`}
          >
            {addingToCart === item.id ? (
              <>
                <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                <span>Added!</span>
              </>
            ) : (
              <>
                <Plus className="w-5 h-5" />
                <span>Add to Cart</span>
              </>
            )}
          </button>
        )}
      </div>
    </div>
  );
};

export default MenuItemCard;
